import { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import useRerender from '../../Common/components/hooks/useRerender';
import Table, { createColumnHelper } from '../../Common/components/Table';
import useDeviceKeys from './hooks/useDeviceKeys';
import moment from 'moment-timezone';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import GroupDeviceKeyActions from './GroupDeviceKeyActions';
import Loading from '@/src/Common/components/Loading';
import { Button } from '@/src/shadComponents/ui/button';

const columnHelper = createColumnHelper<DeviceKey>();

export default function GroupDeviceKeys() {
    const { groupId } = useParams();
    const { orgId } = useUserOrg();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [deviceKeys, setDeviceKeys] = useState<DeviceKey[]>([]);
    const [group, setGroup] = useState<DeviceGroup | null>(null);
    const { getDeviceKeysForGroup } = useDeviceKeys(orgId);

    const { render, renderCount } = useRerender();

    const onDelete = () => {
        render();
    };
    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const deviceKeys = await getDeviceKeysForGroup(+(groupId || 0));
                setDeviceKeys(deviceKeys.keys);
                setGroup(deviceKeys.group);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [renderCount]);

    const columnDefinitions = [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => <span>name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('keyId', {
            id: 'keyId',
            header: () => <span>keyId</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('lastUsed', {
            id: 'lastUsed',
            header: () => <span>last seen</span>,
            cell: (info) => (
                <span>
                    {info.getValue()
                        ? `${moment(info.getValue()).toNow(true)} ago`
                        : ''}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                <GroupDeviceKeyActions
                    orgId={orgId}
                    row={props.row}
                    groupId={groupId ? +groupId : 0}
                    onDelete={onDelete}
                />
            ),
        }),
    ];

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    return (
        <div>
            <div className="row flex mx-8 my-4">
                <h1 className="text-base font-semibold leading-7 text-gray-900">
                    <div className="text-sm leading-6 text-gray-500">
                        <span className="text-gray-700">Device Group</span>
                    </div>
                    <div className="mt-1 text-2xl font-semibold leading-6 text-gray-900">
                        {group?.name}
                    </div>
                </h1>
                <div className="spacer"></div>
                <div className="flex space-x-4">
                    <NavLink
                        to={`/device-management/device-groups/${groupId}/device-keys/assign`}
                    >
                        <Button className="Primary-button">
                            Assign Device Key
                        </Button>
                    </NavLink>
                    <NavLink
                        to={`/device-management/device-groups/${groupId}/device-keys/create`}
                    >
                        <Button className="Primary-button">
                            Create New Device Key
                        </Button>
                    </NavLink>
                </div>
            </div>
            <br />
            <Table data={deviceKeys} columns={columnDefinitions} />
        </div>
    );
}
